import React, { useState } from 'react';
import { Autoplay, EffectFade } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';

import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import 'swiper/css/effect-fade';

import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useConfig } from '../../../utils/useConfig';

function Banner() {
  const [activeIndex, setActiveIndex] = useState(0);
  const config = useConfig();
  const banners = config.banners.home;

  const navigate = useNavigate();

  const handleSlideChange = (swiper: any) => {
    setActiveIndex(swiper.activeIndex);
  };

  const buttonVariants = {
    initial: { scale: 0.95, opacity: 0 },
    animate: {
      scale: 1,
      opacity: 1,
      transition: { duration: 0.3, type: 'spring' },
    },
    hover: {
      scale: 1.05,
      boxShadow: '0 5px 15px rgba(0, 0, 0, 0.1)',
      transition: { duration: 0.2 },
    },
    tap: { scale: 0.95 },
  };

  if (!banners.length) {
    return null;
  }

  return (
    <div className="relative w-full">
      {/* Główny slider */}
      <Swiper
        spaceBetween={0}
        slidesPerView={1}
        centeredSlides={true}
        autoplay={{
          delay: 5000,
          disableOnInteraction: true,
        }}
        effect={'fade'}
        pagination={{
          clickable: true,
          dynamicBullets: true,
        }}
        modules={[Autoplay, EffectFade]}
        onSlideChange={handleSlideChange}
        className="w-full h-[300px] sm:h-[400px] md:h-[500px] lg:h-[600px]"
      >
        {banners.map((banner, index) => (
          <SwiperSlide key={banner.id} className="relative">
            {/* Gradient nakładka na zdjęcie */}
            <div className="absolute inset-0 bg-gradient-to-r from-gray-900/70 to-transparent z-10"></div>

            {/* Zdjęcie tła */}
            <img
              src={banner.image}
              alt={banner.title}
              className="w-full h-full object-cover"
            />

            {/* Treść banera */}
            <div
              className={`absolute inset-0 z-20 flex flex-col justify-center p-6 md:p-12 lg:p-24 ${banner.position === 'left' ? 'items-start' : 'items-end'}`}
            >
              <AnimatePresence mode="wait">
                {activeIndex === index && (
                  <motion.div
                    key={`content-${banner.id}`}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.5 }}
                    className={`max-w-xl flex flex-col ${banner.position === 'left' ? 'items-start' : 'items-end'}`}
                  >
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: '100px' }}
                      transition={{ delay: 0.2, duration: 0.7 }}
                      className="h-1 bg-primary mb-4"
                    ></motion.div>

                    <motion.h2
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.3, duration: 0.7 }}
                      className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-3 leading-tight"
                    >
                      {banner.title}
                    </motion.h2>

                    <motion.p
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 0.5, duration: 0.7 }}
                      className="text-base md:text-lg text-gray-200 mb-6 max-w-md"
                    >
                      {banner.subtitle}
                    </motion.p>

                    <motion.button
                      onClick={() => {
                        navigate(banner.cta.link);
                      }}
                      className={`rounded-full px-5 py-3 flex items-center gap-2 font-medium bg-primary text-white hover:bg-primary/90`}
                      variants={buttonVariants}
                      initial="initial"
                      animate="animate"
                      whileHover={'hover'}
                      whileTap={'tap'}
                    >
                      <span className="mr-2">{banner.cta.text}</span>
                      <ArrowForwardIcon className="text-sm transition-transform group-hover:translate-x-1" />
                    </motion.button>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </SwiperSlide>
        ))}
      </Swiper>

      {/* Dodatkowe wskaźniki i nakładka */}
      <div className="absolute bottom-0 left-0 w-full z-30">
        <div className="flex justify-between items-end max-w-7xl mx-auto px-4 pb-6">
          <div className="hidden md:flex gap-3">
            {banners.map((_, idx) => (
              <motion.div
                key={`indicator-${idx}`}
                className={`h-1 rounded-full bg-white ${idx === activeIndex ? 'w-12 opacity-100' : 'w-3 opacity-50'}`}
                animate={{
                  width: idx === activeIndex ? '48px' : '12px',
                  opacity: idx === activeIndex ? 1 : 0.5,
                }}
                transition={{ duration: 0.4 }}
              ></motion.div>
            ))}
          </div>

          <div className="bg-white/10 backdrop-blur-sm p-2 rounded-lg">
            <div className="text-xs text-white font-medium">
              {activeIndex + 1}/{banners.length}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Banner;
